"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useState, useCallback } from "react"
import {
  HelpCircle,
  Building2,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  MoreHorizontal,
  Archive,
  Trash2,
  ChevronDown,
  Moon,
  Sun,
  LogOut,
  User,
  ArrowRight,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useSidebarStore } from "@/lib/stores/sidebar-store"
import { useDemoStore, DEMO_TENANT } from "@/lib/stores/demo-store"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/lib/hooks/use-auth"
import { Logo } from "@/components/ui/logo"
import { normalizeConversations } from "@/lib/utils/conversation-normalizer"
import { NavIcon } from "@/components/ui/nav-icons/nav-icon"
import { useTheme } from "@/components/theme-provider"
import { createClient } from "@/lib/supabase/client"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"

// Changed display labels: "Brain" → "Memories", "Dashboards" → "Numbers"
// Route hrefs and iconNames remain unchanged (internal architecture stability)
const coreNavItems = [
  {
    title: "Advisor",
    href: "/advisor",
    iconName: "advisor" as const,
    tooltip: "Chat with your AI Business Advisor",
  },
  {
    title: "Goals",
    href: "/goals",
    iconName: "goals" as const,
    tooltip: "Track goals and progress",
  },
  {
    title: "Memories",
    href: "/brain",
    iconName: "brain" as const,
    tooltip: "Manage your business memories",
  },
]

interface RecentChat {
  id: string
  title: string
  updated_at: string
  created_at?: string
  archived_at?: string | null
  deleted_at?: string | null
}

interface SidebarProps {
  isMobile?: boolean
}

export function Sidebar({ isMobile }: SidebarProps) {
  const pathname = usePathname()
  const router = useRouter()
  const { isCollapsed, toggleCollapsed, closeLeftSidebar } = useSidebarStore()
  const [recentChats, setRecentChats] = useState<RecentChat[]>([])
  const [isLoadingChats, setIsLoadingChats] = useState(true)
  const { isDemoMode } = useDemoStore()
  const { user, loading: authLoading } = useAuth()
  const [orgName, setOrgName] = useState<string | null>(null)
  const [isLoadingOrg, setIsLoadingOrg] = useState(true)
  const [isConversationsExpanded, setIsConversationsExpanded] = useState(false)
  const [isRailHovered, setIsRailHovered] = useState(false)

  const { theme, setTheme } = useTheme()
  const [userData, setUserData] = useState<UserData>({
    email: "",
    name: "User",
    initials: "U",
  })

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return

      const supabase = createClient()
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser()

      if (authUser) {
        const email = authUser.email || ""
        const fullName = authUser.user_metadata?.full_name || authUser.user_metadata?.name || email.split("@")[0]
        const firstName = fullName.split(" ")[0]

        const initials =
          fullName
            .split(" ")
            .map((n: string) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2) ||
          email[0]?.toUpperCase() ||
          "U"

        setUserData({
          email,
          name: firstName,
          initials,
        })
      }
    }

    if (!authLoading && user) {
      fetchUserData()
    }
  }, [authLoading, user])

  const handleSignOut = async () => {
    const supabase = createClient()
    await supabase.auth.signOut()
    router.push("/login")
  }

  const fetchOrgName = useCallback(async () => {
    if (!user) {
      setOrgName(null)
      setIsLoadingOrg(false)
      return
    }

    try {
      const response = await fetch("/api/tenant")
      if (response.ok) {
        const { tenant } = await response.json()
        setOrgName(tenant?.name || null)
      } else {
        setOrgName(null)
      }
    } catch (error) {
      console.error("[v0] Failed to fetch org name:", error)
      setOrgName(null)
    } finally {
      setIsLoadingOrg(false)
    }
  }, [user])

  useEffect(() => {
    if (!authLoading && user) {
      fetchOrgName()
    }
  }, [authLoading, user, fetchOrgName])

  useEffect(() => {
    const handleOrgNameUpdated = () => {
      console.log("[v0] Org name updated event received, refreshing...")
      fetchOrgName()
    }
    window.addEventListener("org-name-updated", handleOrgNameUpdated)
    return () => window.removeEventListener("org-name-updated", handleOrgNameUpdated)
  }, [fetchOrgName])

  const fetchRecentChats = useCallback(async () => {
    console.log("[p0][recents] auth status:", {
      loading: authLoading ? "loading" : "ready",
      userId: user ? hashForLog(user.id) : "null",
      timestamp: new Date().toISOString(),
    })

    if (authLoading) {
      console.log("[p0][recents] auth still loading, waiting...")
      return
    }

    if (!user) {
      console.log("[p0][recents] no user, showing empty state")
      setRecentChats([])
      setIsLoadingChats(false)
      return
    }

    setIsLoadingChats(true)

    try {
      console.log("[p0][recents] fetch:start", {
        userId: hashForLog(user.id),
        timestamp: new Date().toISOString(),
      })

      const response = await fetch("/api/conversations", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
      })

      console.log("[p0][recents] fetch:response", {
        status: response.status,
        ok: response.ok,
      })

      if (response.ok) {
        const { conversations } = await response.json()

        const beforeCount = conversations?.length ?? 0

        const normalized = normalizeConversations(conversations || [])

        const activeConversations = normalized.filter((c: RecentChat) => {
          const notArchived = !c.archived_at && !c.deleted_at
          // Server should have already filtered, but double-check
          return notArchived
        })

        const afterCount = activeConversations.length

        console.log("[p0][recents] fetch:filter", {
          beforeCount,
          afterCount,
          filtered: beforeCount - afterCount,
          note: "Server should have already filtered to conversations with messages",
        })

        const sortedConversations = [...activeConversations].sort((a, b) => {
          const aTime = new Date(a.updated_at || a.created_at || 0).getTime()
          const bTime = new Date(b.updated_at || b.created_at || 0).getTime()
          return bTime - aTime
        })

        const firstFiveIds = sortedConversations.slice(0, 5).map((c: RecentChat) => hashForLog(c.id))

        console.log("[p0][recents] fetch:ok", {
          count: afterCount,
          firstFiveIds,
          timestamp: new Date().toISOString(),
        })

        setRecentChats(sortedConversations)
      } else {
        console.warn("[p0][recents] fetch:err", {
          status: response.status,
          statusText: response.statusText,
        })
        setRecentChats([])
      }
    } catch (error) {
      console.error("[p0][recents] fetch:err exception", {
        error: error instanceof Error ? error.message : "Unknown",
      })
      setRecentChats([])
    } finally {
      setIsLoadingChats(false)
    }
  }, [user, authLoading])

  useEffect(() => {
    if (!authLoading) {
      console.log("[v0] [Sidebar] Auth resolved, fetching conversations...")
      fetchRecentChats()
    } else {
      console.log("[v0] [Sidebar] Waiting for auth to resolve...")
    }
  }, [authLoading, user?.id])

  useEffect(() => {
    let debounceTimer: NodeJS.Timeout | null = null

    const handleConversationUpdated = () => {
      console.log("[P0-CONVERSATIONS] Sidebar: Received refresh event, debouncing...")

      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      debounceTimer = setTimeout(() => {
        console.log("[P0-CONVERSATIONS] Sidebar: Debounce complete, refreshing...")
        fetchRecentChats()
      }, 500)
    }

    window.addEventListener("refresh-conversations", handleConversationUpdated)
    window.addEventListener("conversation-created", handleConversationUpdated)

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
      window.removeEventListener("refresh-conversations", handleConversationUpdated)
      window.removeEventListener("conversation-created", handleConversationUpdated)
    }
  }, [fetchRecentChats])

  const handleArchive = async (chatId: string) => {
    console.log(`[CONV-LIFECYCLE] action=archive conv=${hashForLog(chatId)} status=starting`)

    setRecentChats((prev) => prev.filter((chat) => chat.id !== chatId))

    try {
      const response = await fetch(`/api/conversations`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: chatId, action: "archive" }),
      })

      if (response.ok) {
        const data = await response.json()
        console.log(`[CONV-LIFECYCLE] action=archive conv=${hashForLog(chatId)} status=success`, data)
      } else {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }))
        console.error(
          `[CONV-LIFECYCLE] action=archive conv=${hashForLog(chatId)} status=failed error=${errorData.error}`,
        )
        await fetchRecentChats()
      }
    } catch (error) {
      console.error(`[CONV-LIFECYCLE] action=archive conv=${hashForLog(chatId)} status=error`, error)
      await fetchRecentChats()
    }
  }

  const handleDelete = async (chatId: string) => {
    console.log(`[CONV-LIFECYCLE] action=delete conv=${hashForLog(chatId)} status=starting`)

    setRecentChats((prev) => prev.filter((chat) => chat.id !== chatId))

    try {
      const response = await fetch(`/api/conversations`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: chatId, action: "delete" }),
      })

      if (response.ok) {
        const data = await response.json()
        console.log(`[CONV-LIFECYCLE] action=delete conv=${hashForLog(chatId)} status=success`, data)
      } else {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }))
        console.error(
          `[CONV-LIFECYCLE] action=delete conv=${hashForLog(chatId)} status=failed error=${errorData.error}`,
        )
        await fetchRecentChats()
      }
    } catch (error) {
      console.error(`[CONV-LIFECYCLE] action=delete conv=${hashForLog(chatId)} status=error`, error)
      await fetchRecentChats()
    }
  }

  const handleLinkClick = () => {
    if (isMobile) {
      closeLeftSidebar()
    }
  }

  const displayOrgName = isDemoMode ? DEMO_TENANT.name : orgName || (isLoadingOrg ? "Loading..." : "Organization")

  if (isCollapsed && !isMobile) {
    return (
      <TooltipProvider>
        <aside
          className={cn(
            "flex h-full w-16 flex-col items-center bg-background relative transition-colors",
            isRailHovered && "bg-[var(--surface)]/30",
          )}
          onMouseEnter={() => setIsRailHovered(true)}
          onMouseLeave={() => setIsRailHovered(false)}
          onClick={(e) => {
            // Only expand if clicking the rail itself, not buttons/nav items
            if (e.target === e.currentTarget) {
              toggleCollapsed()
            }
          }}
        >
          {/* Header with logo */}
          <div className="flex h-16 items-center justify-center pb-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 relative z-20"
              onClick={(e) => {
                e.stopPropagation() // Prevent rail click from firing
                toggleCollapsed()
              }}
            >
              {isRailHovered ? (
                <ArrowRight className="h-6 w-6" strokeWidth={1.5} />
              ) : (
                <PanelLeft className="h-6 w-6" strokeWidth={1.5} />
              )}
            </Button>
          </div>

          {/* Nav items */}
          <nav className="flex flex-col items-center gap-2 p-2 pt-4 relative z-20">
            {coreNavItems.map((item) => {
              const isActive = pathname === item.href || pathname.startsWith(item.href + "/")

              return (
                <Tooltip key={item.title}>
                  <TooltipTrigger asChild>
                    <Link
                      href={item.href}
                      onClick={(e) => {
                        e.stopPropagation() // Prevent rail click from firing
                        handleLinkClick()
                      }}
                      className={cn(
                        "flex h-11 w-11 items-center justify-center rounded-lg transition-colors group",
                        isActive
                          ? "bg-[var(--surface)]"
                          : "text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:text-[var(--text-primary)]",
                      )}
                    >
                      <NavIcon
                        name={item.iconName}
                        active={isActive}
                        className={cn("h-5 w-5", isActive && "text-[var(--cta)]")}
                      />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">{item.title}</TooltipContent>
                </Tooltip>
              )
            })}
          </nav>

          <div className="flex-1" />

          <div className="p-3 mt-auto relative z-20">
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()} // Prevent rail click from firing
                      className={cn(
                        "flex h-11 w-11 mx-auto items-center justify-center rounded-lg transition-colors",
                        "hover:bg-[var(--surface)]",
                      )}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-slate-600/60 text-slate-300 hover:bg-slate-200 hover:text-slate-900 focus-visible:bg-slate-200 focus-visible:text-slate-900 transition-colors text-sm font-medium">
                          {userData.initials}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">{displayOrgName}</TooltipContent>
              </Tooltip>

              <DropdownMenuContent
                align="end"
                side="right"
                sideOffset={8}
                className="w-56 bg-[var(--popover-elevated)] border border-[var(--border)] shadow-lg"
              >
                {/* Organization Section */}
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{displayOrgName}</p>
                  <p className="text-xs text-[var(--text-tertiary)]">{userData.name}</p>
                </div>
                <DropdownMenuSeparator />

                {/* Organization Settings */}
                <DropdownMenuItem asChild>
                  <Link href="/settings/organization" onClick={handleLinkClick}>
                    <Building2 className="h-4 w-4 mr-2" />
                    Organization Settings
                  </Link>
                </DropdownMenuItem>

                {/* User Settings */}
                <DropdownMenuItem asChild>
                  <Link href="/settings" onClick={handleLinkClick}>
                    <User className="h-4 w-4 mr-2" />
                    User Settings
                  </Link>
                </DropdownMenuItem>

                {/* Theme Toggle */}
                <DropdownMenuItem onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                  {theme === "dark" ? (
                    <>
                      <Sun className="h-4 w-4 mr-2" />
                      Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="h-4 w-4 mr-2" />
                      Dark Mode
                    </>
                  )}
                </DropdownMenuItem>

                {/* Help */}
                <DropdownMenuItem asChild>
                  <Link href="/help" onClick={handleLinkClick}>
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Help
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {/* Sign Out */}
                <DropdownMenuItem onClick={handleSignOut} className="text-[var(--danger)]">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>
      </TooltipProvider>
    )
  }

  return (
    <TooltipProvider>
      <aside className={cn("flex h-full flex-col bg-background", isMobile ? "w-full" : "w-64")}>
        {/* Header with logo */}
        <div className="flex h-16 items-center justify-between pl-3 pr-2 pb-1">
          <Link href="/" className="flex items-center">
            <Logo width={110} height={31} />
          </Link>
          {!isMobile && (
            <Button variant="ghost" size="icon" className="h-11 w-11 mr-1" onClick={toggleCollapsed}>
              <PanelLeftClose className="h-6 w-6" strokeWidth={1.5} />
            </Button>
          )}
        </div>

        {/* Updated nav items */}
        <nav className="px-3 pt-2 space-y-1">
          {coreNavItems.map((item) => {
            const isActive = pathname === item.href || pathname.startsWith(item.href + "/")

            return (
              <Link
                key={item.title}
                href={item.href}
                onClick={handleLinkClick}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors group",
                  isActive
                    ? "bg-[var(--surface)] text-[var(--text-primary)]"
                    : "text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:text-[var(--text-primary)]",
                )}
              >
                <span className={cn(isActive && "text-[var(--cta)]")}>
                  <NavIcon name={item.iconName} active={isActive} className="h-5 w-5 shrink-0" />
                </span>
                <span className="flex-1">{item.title}</span>
              </Link>
            )
          })}
        </nav>

        <div className="flex-1 overflow-hidden px-3 py-4 mt-6 flex flex-col">
          <button
            onClick={() => setIsConversationsExpanded(!isConversationsExpanded)}
            className="flex items-center justify-between px-3 mb-2 text-xs font-medium uppercase tracking-wider text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors w-full"
          >
            <span>Conversations</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                isConversationsExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
          </button>

          {isConversationsExpanded && (
            <div className="space-y-0.5 overflow-y-auto flex-1">
              {authLoading || (isLoadingChats && recentChats.length === 0) ? (
                <div className="px-3 py-2">
                  <p className="text-xs text-[var(--text-tertiary)]">
                    {authLoading ? "Authenticating..." : "Loading..."}
                  </p>
                </div>
              ) : recentChats.length > 0 ? (
                recentChats.map((chat) => (
                  <div
                    key={chat.id}
                    className={cn(
                      "group relative flex items-center rounded-lg pr-12 transition-colors hover:bg-[var(--surface)]",
                      pathname === `/advisor/${chat.id}` && "bg-[var(--surface)]",
                    )}
                  >
                    <Link
                      href={`/advisor/${chat.id}`}
                      onClick={handleLinkClick}
                      className={cn(
                        "flex-1 min-w-0 px-3 py-2 text-sm transition-colors",
                        pathname === `/advisor/${chat.id}`
                          ? "text-[var(--text-primary)]"
                          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                      )}
                      title={chat.title || "Untitled"}
                    >
                      <span className="truncate block">{chat.title || "Untitled"}</span>
                    </Link>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="Conversation actions"
                          aria-haspopup="menu"
                          className={cn(
                            "absolute right-2 top-1/2 -translate-y-1/2",
                            "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                            "opacity-0 pointer-events-none transition-opacity",
                            "group-hover:opacity-100 group-hover:pointer-events-auto",
                            "group-focus-within:opacity-100 group-focus-within:pointer-events-auto",
                            "data-[state=open]:opacity-100 data-[state=open]:pointer-events-auto",
                            "hover:bg-[var(--surface)]",
                            "z-20",
                          )}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align="end" sideOffset={6}>
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault()
                            handleArchive(chat.id)
                          }}
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          Archive
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          className="text-destructive"
                          onSelect={(e) => {
                            e.preventDefault()
                            handleDelete(chat.id)
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))
              ) : (
                <div className="px-3 py-2">
                  <p className="text-sm text-[var(--text-secondary)]">No recent chats</p>
                  {!authLoading && !isLoadingChats && (
                    <p className="text-xs text-[var(--text-tertiary)] mt-1">Check console for [p0][recents] logs</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-3 mb-2">
          <div className="h-px bg-gradient-to-r from-transparent via-[var(--border)] to-transparent" />
        </div>

        <div className="p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-full items-center gap-3 rounded-lg p-3 transition-colors hover:bg-[var(--surface)] text-left">
                {/* Avatar Section */}
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarFallback className="bg-slate-600/60 text-slate-300 hover:bg-slate-200 hover:text-slate-900 focus-visible:bg-slate-200 focus-visible:text-slate-900 transition-colors text-sm font-medium">
                    {userData.initials}
                  </AvatarFallback>
                </Avatar>

                {/* Identity Text */}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate text-foreground">{displayOrgName}</p>
                  <p className="text-xs truncate text-[var(--text-tertiary)]">{userData.name}</p>
                </div>

                <ChevronRight className="h-5 w-5 text-[var(--text-tertiary)] shrink-0" strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              side="top"
              sideOffset={8}
              className="w-56 bg-[var(--popover-elevated)] border border-[var(--border)] shadow-lg"
            >
              {/* Organization + User Header */}
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{displayOrgName}</p>
                <p className="text-xs text-[var(--text-tertiary)]">{userData.name}</p>
              </div>
              <DropdownMenuSeparator />

              {/* Organization Settings */}
              <DropdownMenuItem asChild>
                <Link href="/settings/organization" onClick={handleLinkClick} className="cursor-pointer">
                  <Building2 className="h-4 w-4 mr-2" />
                  Organization Settings
                </Link>
              </DropdownMenuItem>

              {/* User Settings */}
              <DropdownMenuItem asChild>
                <Link href="/settings" onClick={handleLinkClick} className="cursor-pointer">
                  <User className="h-4 w-4 mr-2" />
                  User Settings
                </Link>
              </DropdownMenuItem>

              {/* Theme Toggle */}
              <DropdownMenuItem onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                {theme === "dark" ? (
                  <>
                    <Sun className="h-4 w-4 mr-2" />
                    Light Mode
                  </>
                ) : (
                  <>
                    <Moon className="h-4 w-4 mr-2" />
                    Dark Mode
                  </>
                )}
              </DropdownMenuItem>

              {/* Help */}
              <DropdownMenuItem asChild>
                <Link href="/help" onClick={handleLinkClick} className="cursor-pointer">
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Help
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* Sign Out */}
              <DropdownMenuItem onClick={handleSignOut} className="text-[var(--danger)] cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </TooltipProvider>
  )
}

interface UserData {
  email: string
  name?: string
  initials: string
}

function hashForLog(id: string): string {
  return id.slice(-8)
}
